import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * CurrencyOverridePattern - Replace default gold with campaign-specific currency.
 *
 * Used by Motherboard (quantum) and potentially other frames.
 *
 * Config properties:
 * - displayName: currency name (e.g., 'Quantum')
 * - icon: emoji icon
 * - colorClass: Tailwind classes
 * - currencyKey: character state key (e.g., 'quantum')
 * - startingAmount: default starting value
 * - conversionSource: path to conversion table in frameStaticData
 * - scrapCategories: path to scrap categories for buy/sell
 * - shopStockConfig: dice for randomizing shop stock
 */
const CurrencyOverridePattern = ({
  config,
  character,
  updateCharacter,
  frameStaticData,
  onClose,
}) => {
  const currencyKey = config.currencyKey || 'quantum';
  const currentBalance = character?.[currencyKey] ?? config.startingAmount ?? 0;

  const [adjustAmount, setAdjustAmount] = useState(1);
  const [activeTab, setActiveTab] = useState('balance'); // balance | shop | convert
  const [shopStock, setShopStock] = useState(null);
  const [selectedScrapCategory, setSelectedScrapCategory] = useState(null);

  // Resolve scrap data from static data
  const scrapData = frameStaticData?.customMechanics?.scrap;
  const conversionData = frameStaticData?.customMechanics?.quantumEconomy?.conversion || [];

  // ---------------------------------------------------------------------------
  // Currency actions
  // ---------------------------------------------------------------------------

  const handleAdjust = useCallback((delta) => {
    const newBalance = Math.max(0, currentBalance + delta);
    updateCharacter({ ...character, [currencyKey]: newBalance });
    const verb = delta > 0 ? 'Gained' : 'Spent';
    toast.success(`${verb} ${Math.abs(delta)} ${config.displayName || 'currency'}. Balance: ${newBalance}`);
  }, [currentBalance, character, currencyKey, updateCharacter, config.displayName]);

  // ---------------------------------------------------------------------------
  // Shop stock generation
  // ---------------------------------------------------------------------------

  const rollDie = (dieStr) => {
    const sides = parseInt(dieStr.replace('d', ''), 10) || 6;
    return Math.ceil(Math.random() * sides);
  };

  const generateShopStock = useCallback(() => {
    if (!scrapData?.categories) return;

    const stock = {};
    for (const [categoryKey, category] of Object.entries(scrapData.categories)) {
      const stockDie = categoryKey === 'shards' ? 'd10' : categoryKey === 'metals' ? 'd8' : 'd6';
      const quantity = rollDie(stockDie);
      stock[categoryKey] = {
        die: category.die,
        items: category.items,
        available: quantity,
      };
    }
    setShopStock(stock);
    toast.success('Shop stock refreshed!');
  }, [scrapData]);

  // ---------------------------------------------------------------------------
  // Buy/sell scrap
  // ---------------------------------------------------------------------------

  const handleBuyScrap = useCallback((categoryKey, item) => {
    const price = item.value || item.roll || 1;
    if (currentBalance < price) {
      toast.error(`Not enough ${config.displayName || 'currency'}! Need ${price}.`);
      return;
    }

    if (shopStock && shopStock[categoryKey]?.available <= 0) {
      toast.error('Out of stock!');
      return;
    }

    // Deduct currency
    const scrapInventory = character.inventory?.scrap || {};
    const categoryInventory = scrapInventory[categoryKey] || [];

    updateCharacter({
      ...character,
      [currencyKey]: currentBalance - price,
      inventory: {
        ...character.inventory,
        scrap: {
          ...scrapInventory,
          [categoryKey]: [...categoryInventory, { ...item, id: `scrap-${Date.now()}` }],
        },
      },
    });

    // Reduce shop stock
    if (shopStock) {
      setShopStock(prev => ({
        ...prev,
        [categoryKey]: { ...prev[categoryKey], available: prev[categoryKey].available - 1 },
      }));
    }

    toast.success(`Bought ${item.name} for ${price} quantum`);
  }, [currentBalance, character, currencyKey, updateCharacter, shopStock, config.displayName]);

  const handleSellScrap = useCallback((categoryKey, itemIndex) => {
    const scrapInventory = character.inventory?.scrap || {};
    const categoryInventory = [...(scrapInventory[categoryKey] || [])];
    if (itemIndex >= categoryInventory.length) return;

    const item = categoryInventory[itemIndex];
    const price = item.value || item.roll || 1;

    categoryInventory.splice(itemIndex, 1);

    updateCharacter({
      ...character,
      [currencyKey]: currentBalance + price,
      inventory: {
        ...character.inventory,
        scrap: {
          ...scrapInventory,
          [categoryKey]: categoryInventory,
        },
      },
    });

    toast.success(`Sold ${item.name} for ${price} quantum`);
  }, [character, currencyKey, currentBalance, updateCharacter]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const scrapInventory = character?.inventory?.scrap || {};

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-purple-400 mb-1">
          {config.icon || '💠'} {config.displayName || 'Currency'}
        </h3>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2">
        {['balance', 'shop', 'convert'].map((tab) => (
          <DomainCardButton
            key={tab}
            onClick={() => setActiveTab(tab)}
            variant={activeTab === tab ? 'active' : 'secondary'}
            className="flex-1 capitalize"
          >
            {tab}
          </DomainCardButton>
        ))}
      </div>

      {/* Balance Tab */}
      {activeTab === 'balance' && (
        <div className="space-y-4">
          <div className={`p-6 rounded-lg border-2 text-center ${config.colorClass || 'bg-purple-900/20 border-purple-600'}`}>
            <p className="text-5xl font-bold text-purple-400 mb-2">{currentBalance}</p>
            <p className="text-sm text-purple-300">{config.displayName || 'Currency'}</p>
          </div>

          {/* Quick adjust */}
          <div className="flex items-center gap-2">
            <DomainCardButton onClick={() => handleAdjust(-adjustAmount)} variant="attack" className="flex-1">
              − {adjustAmount}
            </DomainCardButton>
            <select
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(parseInt(e.target.value))}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm text-gray-200"
            >
              {[1, 5, 10, 50, 100].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <DomainCardButton onClick={() => handleAdjust(adjustAmount)} variant="activeSuccess" className="flex-1">
              + {adjustAmount}
            </DomainCardButton>
          </div>

          {/* Scrap inventory summary */}
          {Object.keys(scrapInventory).length > 0 && (
            <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600">
              <p className="text-sm font-medium text-gray-300 mb-2">Scrap Inventory</p>
              {Object.entries(scrapInventory).map(([cat, items]) => (
                <div key={cat} className="flex justify-between text-xs text-gray-400">
                  <span className="capitalize">{cat}</span>
                  <span>{items.length} items</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shop Tab */}
      {activeTab === 'shop' && (
        <div className="space-y-3">
          <DomainCardButton onClick={generateShopStock} variant="primary" className="w-full">
            {shopStock ? 'Reroll Shop Stock' : 'Generate Shop Stock'}
          </DomainCardButton>

          {shopStock && scrapData?.categories && (
            <div className="space-y-3">
              {/* Category selector */}
              <div className="flex gap-2">
                {Object.keys(scrapData.categories).map((cat) => (
                  <DomainCardButton
                    key={cat}
                    onClick={() => setSelectedScrapCategory(cat)}
                    variant={selectedScrapCategory === cat ? 'active' : 'secondary'}
                    className="flex-1 capitalize"
                  >
                    {cat} ({shopStock[cat]?.available || 0})
                  </DomainCardButton>
                ))}
              </div>

              {/* Items for sale */}
              {selectedScrapCategory && shopStock[selectedScrapCategory] && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {shopStock[selectedScrapCategory].items.map((item) => (
                    <div key={item.name} className="flex items-center justify-between bg-gray-700/50 p-2 rounded">
                      <div>
                        <span className="text-sm text-gray-200">{item.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{item.value || item.roll || 1}q</span>
                      </div>
                      <DomainCardButton
                        onClick={() => handleBuyScrap(selectedScrapCategory, item)}
                        variant="primary"
                        className="px-2 py-1"
                        disabled={shopStock[selectedScrapCategory].available <= 0}
                      >
                        Buy
                      </DomainCardButton>
                    </div>
                  ))}
                </div>
              )}

              {/* Sell from inventory */}
              {selectedScrapCategory && (scrapInventory[selectedScrapCategory] || []).length > 0 && (
                <div className="border-t border-gray-600 pt-2">
                  <p className="text-xs text-gray-400 mb-1">Your {selectedScrapCategory}:</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {(scrapInventory[selectedScrapCategory] || []).map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center justify-between bg-gray-800/50 p-2 rounded">
                        <span className="text-sm text-gray-300">{item.name}</span>
                        <DomainCardButton
                          onClick={() => handleSellScrap(selectedScrapCategory, idx)}
                          variant="warning"
                          className="px-2 py-1"
                        >
                          Sell ({item.value || item.roll || 1}q)
                        </DomainCardButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Convert Tab */}
      {activeTab === 'convert' && (
        <div className="space-y-3">
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-600">
            <p className="text-sm font-medium text-gray-300 mb-3">Conversion Table</p>
            {conversionData.map((entry, i) => (
              <div key={i} className="flex justify-between text-sm text-gray-400 py-1 border-b border-gray-700 last:border-0">
                <span>{entry.quantum} quantum</span>
                <span>{entry.gold}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600">
            <p className="text-xs text-gray-400 italic">
              Relics are worth 20 quantum and are often used to craft powerful augments.
            </p>
          </div>
        </div>
      )}

      {onClose && (
        <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
          Close
        </DomainCardButton>
      )}
    </div>
  );
};

export default CurrencyOverridePattern;
